import './HvacFormGroupSchema.scss';
import { Ord as bOrd } from 'fp-ts/boolean';
import * as Eq from 'fp-ts/Eq';
import { pipe } from 'fp-ts/function';
import { Ord as nOrd } from 'fp-ts/number';
import * as Ord from 'fp-ts/Ord';
import * as A from 'fp-ts/ReadonlyArray';
import React, { memo } from 'react';
import { SchemaFile } from 'src/modules/common/components/SchemaFile';
import { ConfigSchemaFile } from 'src/modules/config/types/ConfigSchema';
import { HVAC_SCHEMA_SIZE } from 'src/modules/hvac/constants/schema';
import { HvacConfigSection } from 'src/modules/hvac/types/HvacConfig';
import { HvacSchemaLayer } from 'src/modules/hvac/types/HvacSchema';

type Props = {
  readonly schema: ReadonlyArray<HvacSchemaLayer>;
  readonly focused: HvacConfigSection | null;
};

export const HvacFormGroupSchema = memo(({ schema, focused }: Props) => {
  const output = getOuputLayers(schema, focused);

  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      viewBox={`0 0 ${HVAC_SCHEMA_SIZE.x} ${HVAC_SCHEMA_SIZE.y}`}
      className="bp-hvac-form-group-schema"
    >
      {output.map((layer) => (
        <g
          key={layer.file.fileName}
          opacity={layer.focused ? 1.0 : 0.1}
          className="bp-hvac-form-group-schema__layer"
          data-layer={layer.file.fileName}
          data-source={layer.source}
        >
          <SchemaFile
            file={layer.file.fileName}
            width={HVAC_SCHEMA_SIZE.x}
            height={HVAC_SCHEMA_SIZE.y}
          />
        </g>
      ))}
    </svg>
  );
});

type OutputLayer = {
  readonly file: ConfigSchemaFile;
  readonly focused: boolean;
  readonly source: string;
};

function getOuputLayers(
  layers: ReadonlyArray<HvacSchemaLayer>,
  focused: HvacConfigSection | null,
): OutputLayer[] {
  return pipe(
    layers,
    A.map((layer): OutputLayer => ({
      file: layer.file,
      focused: focused === null || isLayerFocused(layer, focused),
      source: layer.section?.number ?? layer.area ?? 'general',
    })),
    A.sort(ORD_BY_FOCUS),
    A.uniq(EQ_BY_FILE),
    A.sort(ORD_BY_ZINDEX),
    A.toArray,
  );
}

function isLayerFocused(
  layer: HvacSchemaLayer,
  focused: HvacConfigSection,
): boolean {
  if (layer.area !== null && layer.area !== focused.area) {
    return false;
  }
  if (layer.section !== null && !isSectionFocused(layer.section, focused)) {
    return false;
  }

  return true;
}

function isSectionFocused(
  section: HvacConfigSection,
  focused: HvacConfigSection,
): boolean {
  if (section.id === focused.id) {
    return true;
  }
  if (section.id === RELATED_SECTIONS.get(focused.id)) {
    return true;
  }

  return false;
}

const RELATED_SECTIONS: ReadonlyMap<string, string> = new Map([
  ['13', '13a'],
  ['14', '13a'],

  ['33', '33a'],
  ['34', '33a'],
]);

const EQ_BY_FILE: Eq.Eq<OutputLayer> = pipe(
  Eq.eqStrict,
  Eq.contramap((layer) => layer.file.fileName),
);
const ORD_BY_FOCUS: Ord.Ord<OutputLayer> = pipe(
  bOrd,
  Ord.reverse,
  Ord.contramap((layer) => layer.focused),
);
const ORD_BY_ZINDEX: Ord.Ord<OutputLayer> = pipe(
  nOrd,
  Ord.contramap((layer) => layer.file.zIndex),
);
